import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import SharedModal from '../components/common/SharedModal';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { authService, plantService, userService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import PageLoading from '../components/common/PageLoading';
import './Plants.css';

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
  marginTop: '0.5rem',
  border: '1px solid var(--border-primary)'
};

const defaultCenter = {
  lat: 22.3072,
  lng: 73.1812 // Vadodara
};

const libraries = ['places'];

const Plants = () => {
  const [plants, setPlants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [view, setView] = useState('table');
  const [formData, setFormData] = useState({
    plantCode: '',
    plantName: '',
    location: '',
    latitude: '',
    longitude: '',
    status: 'Active',
    plantHeadId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [geoError, setGeoError] = useState(''); // New state for geocoding errors

  // Track if user is manually typing the address to trigger geocoding
  const isTypingLocation = useRef(false);

  // Load Google Maps API (REPLACE 'YOUR_API_KEY_HERE' WITH ACTUAL KEY)
  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "", // ⚠️ INSERT YOUR GOOGLE MAPS API KEY HERE
    libraries
  });

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('Plant.View');
  const canCreate = hasPermission('Plant.Create');
  const canEdit = hasPermission('Plant.Edit');
  const canDelete = hasPermission('Plant.Delete');

  console.log('canView', canView);
  console.log('canCreate', canCreate);
  console.log('canEdit', canEdit);
  console.log('canDelete', canDelete);

  useEffect(() => {
    if (tenantId && canView) {
      // Load cached data immediately if available
      const cachedPlants = sessionStorage.getItem('plants_data');
      const cachedUsers = sessionStorage.getItem('users_data');

      if (cachedPlants) {
        setPlants(JSON.parse(cachedPlants));
        setLoading(false);
      }
      if (cachedUsers) {
        setUsers(JSON.parse(cachedUsers));
      }

      // Load fresh data in background
      loadPlants();
    } else if (!canView) {
      setError('You do not have permission to view plants');
      setLoading(false);
    } else {
      setError('Please login to view plants');
      setLoading(false);
    }
  }, [tenantId, canView]);

  // Geocoding Effect: Update Lat/Lng when Location Address changes
  useEffect(() => {
    // We allow geocoding even if map isn't fully loaded, using OSM fallback
    if (!isTypingLocation.current || !formData.location || formData.location.length < 4) {
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setGeoError('');

      // Strategy 1: Try Google Maps Geocoding
      if (window.google && window.google.maps) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: formData.location }, async (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const location = results[0].geometry.location;
              setFormData(prev => ({
                ...prev,
                latitude: location.lat(),
                longitude: location.lng()
              }));
            } else {
              // Google failed, try Fallback
              console.warn(`Google Geocoding failed (${status}), trying OSM fallback...`);
              await tryOsmGeocoding(formData.location);
            }
          });
          return; // Exit if Google logic was initiated
        } catch (e) {
          console.error("Google Geocoding error", e);
        }
      }

      // Strategy 2: Fallback to OpenStreetMap (Nominatim) immediately if Google not available or failed above
      await tryOsmGeocoding(formData.location);

    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [formData.location, isMapLoaded]);

  // Helper for OSM Geocoding
  const tryOsmGeocoding = async (address) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
      const data = await response.json();

      if (data && data.length > 0) {
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        }));
      } else {
        setGeoError('Location not found.');
      }
    } catch (err) {
      console.error("OSM Geocoding error:", err);
      setGeoError('Could not find location. Please check internet connection.');
    }
  };



  // Detect User Location on Add Plant (Run once when modal opens and map is ready)
  useEffect(() => {
    if (showModal && !editingPlant && isMapLoaded && !formData.latitude) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;

            // Reverse geocode to get address
            if (window.google && window.google.maps) {
              const geocoder = new window.google.maps.Geocoder();
              const latlng = { lat: parseFloat(latitude), lng: parseFloat(longitude) };

              geocoder.geocode({ location: latlng }, (results, status) => {
                let addressValue = '';
                if (status === 'OK' && results && results[0]) {
                  addressValue = results[0].formatted_address;
                }

                setFormData(prev => ({
                  ...prev,
                  latitude: latitude,
                  longitude: longitude,
                  location: addressValue // Auto-fill address
                }));
              });
            }
          },
          (error) => {
            console.warn("Geolocation access denied or failed", error);
            // Fail silently, user can still manually pick
          }
        );
      }
    }
  }, [showModal, editingPlant, isMapLoaded]); // Intentionally omitting formData to run only on open logic

  const loadPlants = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem('plants_data');
      if (!hasCachedData) {
        setLoading(true);
      }
      setError('');
      const [plantsData, usersData] = await Promise.all([
        plantService.getAllPlants(),
        userService.getAllUsers()
      ]);
      setPlants(plantsData);
      setUsers(usersData);

      // Cache the data
      sessionStorage.setItem('plants_data', JSON.stringify(plantsData));
      sessionStorage.setItem('users_data', JSON.stringify(usersData));
    } catch (err) {
      setError('Failed to load plants. Please try again.');
      console.error('Error loading plants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPlants = plants.filter((plant) =>
    plant.plantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.plantCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create plants');
      return;
    }
    setEditingPlant(null);
    isTypingLocation.current = false;
    setFormData({
      plantCode: '',
      plantName: '',
      location: '',
      latitude: '',
      longitude: '',
      status: 'Active',
      plantHeadId: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (plant) => {
    if (!canEdit) {
      setError('You do not have permission to edit plants');
      return;
    }
    setEditingPlant(plant);
    isTypingLocation.current = false;
    setFormData({
      plantCode: plant.plantCode || '',
      plantName: plant.plantName || '',
      location: plant.location || '',
      latitude: plant.latitude || '',
      longitude: plant.longitude || '',
      status: plant.status || 'Active',
      plantHeadId: plant.plantHeadId?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      const toastId = toast.loading("Deleting plant...");
      await plantService.deletePlant(id);
      await loadPlants();
      setDeleteConfirm(null);
      toast.update(toastId, { render: 'Plant deleted successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const msg = 'Failed to delete plant. Please try again.';
      setError(msg);
      toast.error(msg, { closeButton: true });
      console.error('Error deleting plant:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.plantCode.trim()) {
      errors.plantCode = 'Plant code is required';
    } else if (formData.plantCode.length > 20) {
      errors.plantCode = 'Plant code must be less than 20 characters';
    }
    if (!formData.plantName.trim()) {
      errors.plantName = 'Plant name is required';
    } else if (formData.plantName.length > 100) {
      errors.plantName = 'Plant name must be less than 100 characters';
    }
    if (formData.location && formData.location.length > 200) {
      errors.location = 'Location must be less than 200 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingPlant && !canEdit) {
      setError('You do not have permission to edit plants');
      return;
    }
    if (!editingPlant && !canCreate) {
      setError('You do not have permission to create plants');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      isTypingLocation.current = false;
      const toastId = toast.loading(editingPlant ? "Updating plant..." : "Creating plant...");

      const plantData = {
        plantCode: formData.plantCode.trim(),
        plantName: formData.plantName.trim(),
        location: formData.location.trim() || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        status: formData.status,
        plantHeadId: formData.plantHeadId ? parseInt(formData.plantHeadId) : null,
      };

      if (editingPlant) {
        // Update
        plantData.plantId = editingPlant.plantId;
        await plantService.updatePlant(editingPlant.plantId, plantData);
      } else {
        // Create
        await plantService.createPlant(plantData);
      }

      await loadPlants();
      setShowModal(false);
      setFormData({ plantCode: '', plantName: '', location: '', latitude: '', longitude: '', status: 'Active', plantHeadId: '' });
      setEditingPlant(null);
      toast.update(toastId, { render: editingPlant ? 'Plant updated successfully!' : 'Plant created successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        'Failed to save plant. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage, { closeButton: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlant(null);
    setFormData({ plantCode: '', plantName: '', location: '', latitude: '', longitude: '', status: 'Active', plantHeadId: '' });
    setFormErrors({});
  };

  const handleMapClick = useCallback((event) => {
    isTypingLocation.current = false; // User is manually interacting with map
    setFormData(prev => ({
      ...prev,
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng()
    }));
  }, []);

  const handleMarkerDragEnd = useCallback((event) => {
    isTypingLocation.current = false; // User is manually interacting with map
    setFormData(prev => ({
      ...prev,
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng()
    }));
  }, []);

  const getPlantHeadName = (userId) => {
    if (!userId) return '-';
    // Use loose equality to handle string/number type mismatch
    const user = users.find(u => u.userId == userId);
    return user ? user.fullName : '-';
  };

  if (loading) {
    return <PageLoading message="Loading plants..." />;
  }

  return (
    <div className="plants-page">
      <div className="page-header">
        <div className="header-content flex-column">
          <h1>Plants</h1>
          <p>Manage your organization plants</p>
        </div>
        <div className="header-actions">


          {canCreate && (
            <button className="btn-primary" onClick={handleAdd}>
              <HiOutlinePlus />
              Add Plant
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10 kizantable">
        <div className="table-header stacked">
          <div className='left-group'>
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search plants..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>

            <div className='rightbox'>
              <div className="view-toggle">
                <button
                  className={`view-btn ${view === 'table' ? 'active' : ''}`}
                  onClick={() => setView('table')}
                >
                  ☰
                </button>
                <button
                  className={`view-btn ${view === 'grid' ? 'active' : ''}`}
                  onClick={() => setView('grid')}
                >
                  ⊞
                </button>
              </div>

              <span className="plant-count">
                {filteredPlants.length} {filteredPlants.length === 1 ? 'plant' : 'plants'}
              </span>
            </div>
          </div>
        </div>


        {
          view === 'grid' && (
            <div className="plants-grid active">
              {filteredPlants.map(plant => (
                <div className="plant-card" key={plant.plantId}>
                  <div className="plant-header">
                    <div className="plant-icon">🏭</div>
                    <div className="plant-actions">
                      {canEdit && (
                        <div className="action-icon" onClick={() => handleEdit(plant)} title="Edit"><svg stroke="currentColor" fill="none" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"></path></svg></div>
                      )}
                      {canDelete && (
                        <div className="action-icon delete" onClick={() => setDeleteConfirm(plant)} title="Delete"><svg stroke="currentColor" fill="none" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"></path></svg></div>
                      )}
                    </div>
                  </div>

                  <div className="plandidNum">
                    <div className="plant-code">{plant.plantCode}</div>
                    <p className="plant-id">ID: {plant.plantId}</p>
                  </div>
                  <h3 className="plant-name">{plant.plantName}</h3>


                  <div className="plant-location">
                    <span className="location-icon"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6c0 3.5 4.5 8.5 4.5 8.5S12.5 9.5 12.5 6c0-2.5-2-4.5-4.5-4.5z"></path><circle cx="8" cy="6" r="1.5"></circle></svg></span>
                    <span className="location-text">{plant.location || '-'}</span>
                  </div>

                  <div className="plant-footer">
                    <div className={`status-badge ${plant.status === 'Active' ? 'active' : 'inactive'}`}>
                      <span className="status-dot"></span>
                      <span>{plant.status}</span>
                    </div>
                    <span className="plant-date">
                      {plant.createdAt ? new Date(plant.createdAt).toLocaleDateString() : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )
        }

        {
          view === 'table' && (
            filteredPlants?.length === 0 ? (
              <div className="empty-state">
                <HiOutlineBuildingOffice2 className="empty-icon" />
                <h3>No plants found</h3>
                <p>
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'Get started by creating your first plant'}
                </p>
                {!searchTerm && canCreate && (
                  <button className="btn-primary" onClick={handleAdd}>
                    New Plant
                  </button>
                )}
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Plant Code</th>
                      <th>Plant Name</th>
                      <th className='wd255'>Location</th>
                      <th>Plant Head</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlants.map((plant) => (
                      <tr key={plant.plantId}>
                        <td>{plant.plantId}</td>
                        <td className="code-cell">{plant.plantCode}</td>
                        <td className="name-cell">{plant.plantName}</td>
                        <td>{plant.location || '-'}</td>
                        <td>{getPlantHeadName(plant.plantHeadId)}</td>
                        <td>
                          <span className={`status-badge ${plant.status?.toLowerCase()}`}>
                            {plant.status || 'Active'}
                          </span>
                        </td>
                        <td>
                          {plant.createdAt
                            ? new Date(plant.createdAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {canEdit && (
                              <button
                                className="btn-icon-edit"
                                onClick={() => handleEdit(plant)}
                                title="Edit"
                              >
                                <HiOutlinePencil />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn-icon-delete delete"
                                onClick={() => setDeleteConfirm(plant)}
                                title="Delete"
                              >
                                <HiOutlineTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )
        }
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingPlant ? 'Edit Plant' : 'New Plant'}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="plant-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingPlant
                  ? 'Updating...'
                  : 'Creating...'
                : editingPlant
                ? 'Update Plant'
                : 'Create Plant'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="plant-form" onSubmit={handleSubmit} className="modal-form">
            <div className="grid-row two-col">
              <div className='row'>
                <div className='col-md-6'>
                  <div className="form-group">
                    <label htmlFor="plantCode">
                      Plant Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="plantCode"
                      value={formData.plantCode}
                      onChange={(e) =>
                        setFormData({ ...formData, plantCode: e.target.value.toUpperCase() })
                      }
                      className={formErrors.plantCode ? 'error' : ''}
                      placeholder="Enter plant code"
                      maxLength={20}
                    />
                    {formErrors.plantCode && (
                      <span className="field-error">{formErrors.plantCode}</span>
                    )}
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className="form-group">
                    <label htmlFor="plantName">
                      Plant Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="plantName"
                      value={formData.plantName}
                      onChange={(e) =>
                        setFormData({ ...formData, plantName: e.target.value })
                      }
                      className={formErrors.plantName ? 'error' : ''}
                      placeholder="Enter plant name"
                      maxLength={100}
                    />
                    {formErrors.plantName && (
                      <span className="field-error">{formErrors.plantName}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid-row two-col">
              <div className='row'>
                <div className='col-md-6'>
                  <div className="form-group">
                    <label htmlFor="plantHeadId">Plant Head</label>
                    <select
                      id="plantHeadId"
                      value={formData.plantHeadId}
                      onChange={(e) =>
                        setFormData({ ...formData, plantHeadId: e.target.value })
                      }
                    >
                      <option value="">Select Plant Head (Optional)</option>
                      {users.map((user) => (
                        <option key={user.userId} value={user.userId.toString()}>
                          {user.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location">Address / Location</label>
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={(e) => {
                  isTypingLocation.current = true;
                  setGeoError('');
                  const val = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    location: val,
                    // Clear coords if address is cleared
                    latitude: val ? prev.latitude : '',
                    longitude: val ? prev.longitude : ''
                  }));
                }}
                className={formErrors.location ? 'error' : ''}
                placeholder="Enter plant address"
                maxLength={200}
              />
              {formErrors.location && (
                <span className="field-error">{formErrors.location}</span>
              )}
              {geoError && (
                <span className="field-error" style={{ color: '#ef4444' }}>{geoError}</span>
              )}
            </div>

            {/* Geo Options */}
            <div className="geo-section" style={{ marginTop: '1rem', borderTop: '1px solid var(--border-secondary)', paddingTop: '1rem' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>Geographic Coordinates</h4>
              <div className="grid-row two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group">
                  <label>Latitude</label>
                  <input type="number" value={formData.latitude} readOnly placeholder="Select on map" style={{ background: 'var(--bg-tertiary)' }} />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input type="number" value={formData.longitude} readOnly placeholder="Select on map" style={{ background: 'var(--bg-tertiary)' }} />
                </div>
              </div>

              <div className="map-wrapper">
                {loadError ? (
                  <div className="map-error" style={{ padding: '2rem', textAlign: 'center', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                    <p style={{ fontWeight: 600 }}>Map cannot be loaded</p>
                    <p style={{ fontSize: '0.85rem' }}>{loadError.message}</p>
                    <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Please ensure a valid Google Maps API Key is added in <code>src/pages/Plants.js</code></p>
                  </div>
                ) : isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={formData.latitude && formData.longitude
                      ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }
                      : defaultCenter}
                    zoom={12}
                    onClick={handleMapClick}
                  >
                    {(formData.latitude && formData.longitude) && (
                      <Marker
                        position={{ lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                    <PageLoading variant="compact" message="Loading Google Maps..." />
                    {/* Hint for developer if it hangs here */}
                    {/* Hint for developer if it hangs here */}
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }}>
                      If this takes too long, check if the API Key is missing.
                    </p>
                  </div>
                )}
                <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }}>
                  Click on the map to set the plant location. Drag marker to refine.
                </p>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="delete-modal"
      >
        <div className="modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.plantName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The plant will be marked as inactive.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => handleDelete(deleteConfirm?.plantId)}
          >
            Delete
          </button>
        </div>
      </SharedModal>
    </div >
  );
};

export default Plants;
